import { useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, today, addDays, isoDate } from "../format.js";
import {
  Async, Button, Card, Check, ErrorPanel, Field, Fields, Grid, Input, Loading, Modal, Ribbon,
  Select, Sep, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const blankLine = () => ({ key: Math.random().toString(36).slice(2), description: "", qty: 1, rate: 0, taxable: true });

export default function InvoiceEditor({ id }) {
  const { clients, projects, items, settings } = useApp();
  const toast = useToast();

  const existing = useAsync(() => (id ? api.get(`invoices/${id}`) : Promise.resolve(null)), [id]);

  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([blankLine()]);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [savedId, setSavedId] = useState(id || null);
  const [error, setError] = useState(null);

  // Seed the form once the (optional) existing invoice has loaded.
  useEffect(() => {
    if (existing.loading) return;
    const inv = existing.data;
    if (inv) {
      setForm({
        client_id: inv.client_id,
        project_id: inv.project_id || "",
        number: inv.number,
        issue_date: isoDate(inv.issue_date),
        due_date: isoDate(inv.due_date),
        po_number: inv.po_number || "",
        notes: inv.notes || "",
        tax_rate: Number(inv.tax_rate),
        tax_label: inv.tax_label,
        currency_symbol: inv.currency_symbol,
      });
      setLines(inv.lines.length ? inv.lines.map((l) => ({ ...l, key: l.id })) : [blankLine()]);
      setSavedId(inv.id);
    } else {
      const first = clients.find((c) => c.status === "active");
      setForm({
        client_id: first?.id || "",
        project_id: "",
        number: "",
        issue_date: today(),
        due_date: addDays(today(), first?.terms_days ?? settings.default_terms_days),
        po_number: "",
        notes: settings.footer_note,
        tax_rate: Number(settings.tax_rate),
        tax_label: settings.tax_label,
        currency_symbol: settings.currency_symbol,
      });
    }
  }, [existing.loading, existing.data]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
    const taxable = lines
      .filter((l) => l.taxable)
      .reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
    const tax = (taxable * Number(form?.tax_rate || 0)) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [lines, form?.tax_rate]);

  if (existing.loading || !form) return <Loading />;
  if (existing.error) return <ErrorPanel error={existing.error} onRetry={existing.reload} />;

  const sym = form.currency_symbol || "$";
  const client = clients.find((c) => c.id === form.client_id);
  const clientProjects = projects.filter((p) => p.client_id === form.client_id);
  const isDraft = !existing.data || existing.data.status === "draft";

  const editLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) =>
    setLines((ls) => {
      const next = ls.filter((l) => l.key !== key);
      return next.length ? next : [blankLine()];
    });
  const applyItem = (key, itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    editLine(key, {
      description: `${item.name}${item.description ? ` — ${item.description}` : ""}`,
      rate: item.rate,
      taxable: item.taxable,
    });
  };

  const payload = () => ({
    ...form,
    project_id: form.project_id || null,
    lines: lines
      .filter((l) => String(l.description).trim() || Number(l.qty) || Number(l.rate))
      .map((l) => ({ description: l.description, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0, taxable: !!l.taxable })),
  });

  async function save({ then } = {}) {
    setError(null);
    if (!form.client_id) return setError(new Error("Pick a client first."));
    setSaving(true);
    try {
      const saved = savedId ? await api.put(`invoices/${savedId}`, payload()) : await api.post("invoices", payload());
      setSavedId(saved.id);
      setLines(saved.lines.length ? saved.lines.map((l) => ({ ...l, key: l.id })) : [blankLine()]);
      setForm((f) => ({ ...f, number: saved.number }));
      toast.push(`${saved.number} saved as a draft`);
      if (then === "send") setSendOpen(true);
      else navigate(`/invoices/${saved.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  /** Pulling work needs a persisted draft, so save first if this is brand new. */
  async function pull(kind, ids) {
    setError(null);
    setSaving(true);
    try {
      let target = savedId;
      if (!target) {
        const saved = await api.post("invoices", payload());
        target = saved.id;
        setSavedId(saved.id);
        setForm((f) => ({ ...f, number: saved.number }));
      }
      const res = await api.post(`invoices/${target}/${kind}`, ids);
      setLines(res.invoice.lines.map((l) => ({ ...l, key: l.id })));
      toast.push(res.added ? `Added ${res.added} item${res.added === 1 ? "" : "s"}` : "Nothing unbilled to add");
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <TopBar crumb="Get paid / Invoices" title={savedId ? `Edit ${form.number}` : "New invoice"}>
        <Button onClick={() => navigate(savedId ? `/invoices/${savedId}` : "/invoices")}>Cancel</Button>
        <Button busy={saving} onClick={() => save()}>
          Save draft
        </Button>
        <Button kind="pri" busy={saving} onClick={() => save({ then: "send" })}>
          Save &amp; send
        </Button>
      </TopBar>

      <Wrap>
        {error && <ErrorPanel error={error} />}
        {!isDraft && (
          <Ribbon tone="warn">
            This invoice has already been sent, so it can no longer be edited. Void it and issue a replacement
            if something is wrong.
          </Ribbon>
        )}

        <Grid cols="1fr 322px" style={{ alignItems: "start" }}>
          <div style={{ display: "grid", gap: 14 }}>
            <Card title="Details">
              <Fields cols={3}>
                <Field label="Client">
                  <Select
                    value={form.client_id}
                    onChange={(e) => {
                      const c = clients.find((x) => x.id === e.target.value);
                      set("client_id", e.target.value);
                      set("project_id", "");
                      if (c) set("due_date", addDays(form.issue_date, c.terms_days ?? settings.default_terms_days));
                    }}
                  >
                    <option value="">Select a client…</option>
                    {clients
                      .filter((c) => c.status === "active")
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </Select>
                </Field>
                <Field label="Project (optional)">
                  <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}>
                    <option value="">None</option>
                    {clientProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="PO / reference">
                  <Input value={form.po_number} placeholder="Optional" onChange={(e) => set("po_number", e.target.value)} />
                </Field>
              </Fields>

              <Fields cols={3} className="mt">
                <Field label="Invoice number" hint={savedId ? undefined : "Assigned automatically on save"}>
                  <Input value={form.number} placeholder="Auto" onChange={(e) => set("number", e.target.value)} />
                </Field>
                <Field label="Issue date">
                  <Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} />
                </Field>
                <Field label="Due date">
                  <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
                </Field>
              </Fields>
            </Card>

            <Card
              title="Line items"
              bare
              right={
                <>
                  <Button sm onClick={() => pull("pull-time")} busy={saving}>
                    ◷ Pull unbilled time
                  </Button>
                  <Button sm onClick={() => pull("pull-expenses")} busy={saving}>
                    ◰ Add expenses
                  </Button>
                </>
              }
            >
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "38%" }}>Description</th>
                      <th style={{ width: 150 }}>Service</th>
                      <th className="right" style={{ width: 80 }}>
                        Qty
                      </th>
                      <th className="right" style={{ width: 110 }}>
                        Rate
                      </th>
                      <th style={{ width: 52 }}>Tax</th>
                      <th className="right" style={{ width: 110 }}>
                        Amount
                      </th>
                      <th style={{ width: 34 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.key}>
                        <td>
                          <Input
                            value={l.description}
                            placeholder="What are you billing for?"
                            onChange={(e) => editLine(l.key, { description: e.target.value })}
                          />
                        </td>
                        <td>
                          <Select value="" onChange={(e) => applyItem(l.key, e.target.value)}>
                            <option value="">Custom…</option>
                            {items.map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.name}
                              </option>
                            ))}
                          </Select>
                        </td>
                        <td>
                          <Input
                            className="i right num"
                            value={l.qty}
                            onChange={(e) => editLine(l.key, { qty: e.target.value })}
                          />
                        </td>
                        <td>
                          <Input
                            className="i right num"
                            value={l.rate}
                            onChange={(e) => editLine(l.key, { rate: e.target.value })}
                          />
                        </td>
                        <td className="right">
                          <input
                            type="checkbox"
                            checked={!!l.taxable}
                            onChange={(e) => editLine(l.key, { taxable: e.target.checked })}
                          />
                        </td>
                        <td className="right num">{money(Number(l.qty || 0) * Number(l.rate || 0), sym)}</td>
                        <td className="right">
                          <span className="x" style={{ fontSize: 16 }} onClick={() => removeLine(l.key)}>
                            ×
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bd" style={{ borderTop: "1px solid var(--line-2)" }}>
                <Button sm onClick={() => setLines((ls) => [...ls, blankLine()])}>
                  ＋ Add line
                </Button>
              </div>
            </Card>

            <Card title="Notes & terms">
              <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              <div className="tiny muted" style={{ marginTop: 7 }}>
                Defaults come from Settings → Invoice defaults.
              </div>
            </Card>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <Card title="Totals">
              <div style={{ display: "grid", gap: 9 }}>
                <div className="row">
                  <span className="muted sm">Subtotal</span>
                  <div className="grow" />
                  <span className="num">{money(totals.subtotal, sym)}</span>
                </div>
                <div className="row">
                  <span className="muted sm">
                    {form.tax_label} ({form.tax_rate}%)
                  </span>
                  <div className="grow" />
                  <span className="num">{money(totals.tax, sym)}</span>
                </div>
                <Sep />
                <div className="row">
                  <b>Total</b>
                  <div className="grow" />
                  <b className="num" style={{ fontSize: 18 }}>
                    {money(totals.total, sym)}
                  </b>
                </div>
              </div>
            </Card>

            <Card title="Client">
              {client ? (
                <div style={{ display: "grid", gap: 7 }}>
                  <b>{client.name}</b>
                  <div className="sm dim">{client.email || "No email on file"}</div>
                  <Ribbon>Payment terms: net {client.terms_days} days</Ribbon>
                </div>
              ) : (
                <div className="sm muted">Pick a client to see their terms.</div>
              )}
            </Card>
          </div>
        </Grid>
      </Wrap>

      {sendOpen && savedId && (
        <SendModal
          invoiceId={savedId}
          onClose={() => setSendOpen(false)}
          onSent={() => navigate(`/invoices/${savedId}`)}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- send modal */

export function SendModal({ invoiceId, onClose, onSent }) {
  const toast = useToast();
  const state = useAsync(() => api.get(`invoices/${invoiceId}`), [invoiceId]);
  const [form, setForm] = useState({ to: "", subject: "", message: "", attach_pdf: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const inv = state.data;
    if (!inv) return;
    setForm({
      to: inv.client_email || "",
      subject: `Invoice ${inv.number} — ${money(inv.total, inv.currency_symbol)}`,
      // No greeting here — the email template adds "Hi {first name}," itself,
      // so including one in the default message printed it twice.
      message: `Please find invoice ${inv.number} attached.`,
      attach_pdf: true,
    });
  }, [state.data]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`invoices/${invoiceId}/send`, form);
      toast.push(res.mail?.skipped ? "Invoice marked sent (email is not configured yet)" : "Invoice sent");
      onSent?.();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Send invoice"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={send}>
            Send invoice
          </Button>
        </>
      }
    >
      <Async state={state}>
        {() => (
          <Fields>
            {error && <ErrorPanel error={error} />}
            <Field label="To">
              <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </Field>
            <Field label="Subject">
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </Field>
            <Field label="Message">
              <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </Field>
            <Check
              label="Attach the PDF"
              checked={form.attach_pdf}
              onChange={(v) => setForm({ ...form, attach_pdf: v })}
            />
          </Fields>
        )}
      </Async>
    </Modal>
  );
}
